import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiConfig } from '../utils/apiConfig';
import { generateAffiliateUrl, generateMGSAffiliateUrl } from '../utils/affiliateHelper';

const UnifiedSearch = () => {
  const [searchParams] = useSearchParams();
  const [keyword, setKeyword] = useState(searchParams.get('keyword') || '');
  const [source, setSource] = useState('all');
  const [target, setTarget] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // grid or list
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);

  // Auto-search when coming from homepage
  useEffect(() => {
    const urlKeyword = searchParams.get('keyword');
    if (urlKeyword) {
      setKeyword(urlKeyword);
      // Perform initial search
      if (urlKeyword.trim()) {
        setLoading(true);
        setError(null);
        
        apiConfig.getApi().get('/search/unified', {
          params: { keyword: urlKeyword, page: 1, source: 'all', target: 'all' }
        })
        .then(response => {
          setResults(response.data);
          setLoading(false);
        })
        .catch(err => {
          setError('検索に失敗しました');
          console.error(err);
          setLoading(false);
        });
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!keyword.trim()) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiConfig.getApi().get('/search/unified', {
        params: { keyword, page, source, target }
      });
      setResults(response.data);
    } catch (err) {
      setError('検索に失敗しました');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    handleSearch(new Event('submit'));
  };

  const handleSourceChange = (newSource) => {
    setSource(newSource);
    setPage(1);
    if (keyword) {
      handleSearch(new Event('submit'));
    }
  };

  const handleTargetChange = (newTarget) => {
    setTarget(newTarget);
    setPage(1);
    if (keyword) {
      handleSearch(new Event('submit'));
    }
  };

  const getSourceBadge = (video) => {
    if (video.source === 'MGS') {
      return <span className="bg-red-600 text-white px-2 py-1 rounded text-xs font-bold">MGS限定</span>;
    } else if (video.source === 'DMM') {
      return <span className="bg-blue-600 text-white px-2 py-1 rounded text-xs font-bold">FANZA</span>;
    } else if (video.source === 'BOTH') {
      return <span className="bg-purple-600 text-white px-2 py-1 rounded text-xs font-bold">MGS+FANZA</span>;
    }
    return null;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP');
  };

  return (
    <div className="unified-search">
      <div className="mb-8">
        <h2 className="text-3xl font-bold mb-4">検索</h2>
        <p className="text-gray-600">保存済みの作品や女優を検索できます</p>
      </div>

      {/* Search Form */}
      <form onSubmit={handleSearch} className="mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <input
            type="text"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            placeholder="作品名、品番、女優名で検索..."
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <select
            value={source}
            onChange={(e) => handleSourceChange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">すべて</option>
            <option value="dmm">FANZAのみ</option>
            <option value="mgs">MGSのみ</option>
          </select>
          <select
            value={target}
            onChange={(e) => handleTargetChange(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">作品+女優</option>
            <option value="videos">作品のみ</option>
            <option value="actresses">女優のみ</option>
          </select>
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            {loading ? '検索中...' : '検索'}
          </button>
        </div>
      </form>

      {/* View Mode Toggle */}
      {results && (results.videos?.length > 0 || results.actresses?.length > 0) && (
        <div className="mb-4 flex justify-end">
          <div className="inline-flex rounded-lg border border-gray-300">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-4 py-2 text-sm font-medium rounded-l-lg ${
                viewMode === 'grid'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              グリッド表示
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-4 py-2 text-sm font-medium rounded-r-lg ${
                viewMode === 'list'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              リスト表示
            </button>
          </div>
        </div>
      )}

      {/* Source Statistics */}
      {results && results.sources && (
        <div className="mb-6 flex gap-4 text-sm">
          {results.sources.dmm?.count > 0 && (
            <span className="text-blue-600">
              FANZA: {results.sources.dmm.count}件
              {results.sources.dmm.hasMore && '+'}
            </span>
          )}
          {results.sources.mgs?.count > 0 && (
            <span className="text-red-600">
              MGS: {results.sources.mgs.count}件
              {results.sources.mgs.hasMore && '+'}
            </span>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Results */}
      {results && (
        <div>
          {/* Actresses Results */}
          {results.actresses && Array.isArray(results.actresses) && results.actresses.length > 0 && (
            <div className="mb-8">
              <h3 className="text-xl font-bold mb-4">女優</h3>
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                  {results.actresses.map((actress) => (
                    <Link
                      key={actress.id}
                      to={`/actresses/${actress.id}`}
                      className="border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                    >
                      <ActressCard actress={actress} />
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {results.actresses.map((actress) => (
                    <Link
                      key={actress.id}
                      to={`/actresses/${actress.id}`}
                      className="block border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                    >
                      <ActressListItem actress={actress} />
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Videos Results */}
          {results.videos && Array.isArray(results.videos) && results.videos.length > 0 && (
            <div>
              <h3 className="text-xl font-bold mb-4">作品</h3>
              {viewMode === 'grid' ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 mb-8">
                {results.videos.map((video, index) => (
                  <div key={`${video.source}-${video.productCode}-${index}`} 
                       className="border rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow relative">
                    {/* Source Badge */}
                    <div className="absolute top-2 left-2 z-10">
                      {getSourceBadge(video)}
                    </div>
                    
                    {/* Video Link */}
                    {video.id ? (
                      <Link to={`/videos/${video.id}`}>
                        <VideoCard video={video} />
                      </Link>
                    ) : (
                      <VideoCard video={video} />
                    )}

                    {/* Action Buttons */}
                    <div className="p-3 border-t bg-gray-50">
                      <div className="flex gap-2 justify-center">
                        {video.dmmContentId && (
                          <a
                            href={generateAffiliateUrl(video.dmmUrl || `https://www.dmm.co.jp/digital/videoa/-/detail/=/cid=${video.dmmContentId}/`)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                          >
                            FANZA
                          </a>
                        )}
                        {video.mgsUrl && (
                          <a
                            href={generateMGSAffiliateUrl(video.mgsUrl)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                          >
                            MGS
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="space-y-4 mb-8">
                {results.videos.map((video, index) => (
                  <div key={`${video.source}-${video.productCode}-${index}`} 
                       className="border rounded-lg p-4 hover:bg-gray-50 transition-colors">
                    <div className="flex gap-4">
                      {/* Thumbnail */}
                      <div className="flex-shrink-0">
                        {video.coverImage ? (
                          <img 
                            src={video.coverImage} 
                            alt={video.title} 
                            className="w-24 h-32 object-cover rounded"
                          />
                        ) : (
                          <div className="w-24 h-32 bg-gray-200 flex items-center justify-center rounded">
                            <span className="text-gray-500 text-sm">No Image</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Details */}
                      <div className="flex-1">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            {video.id ? (
                              <Link to={`/videos/${video.id}`} className="hover:underline">
                                <h4 className="font-medium text-lg mb-1">{video.title}</h4>
                              </Link>
                            ) : (
                              <h4 className="font-medium text-lg mb-1">{video.title}</h4>
                            )}
                            <p className="text-sm text-gray-600 mb-1">{video.productCode}</p>
                            {video.actresses && video.actresses.length > 0 && (
                              <p className="text-sm text-gray-600 mb-1">
                                出演: {video.actresses.join(', ')}
                              </p>
                            )}
                            {video.releaseDate && (
                              <p className="text-sm text-gray-500">
                                発売日: {formatDate(video.releaseDate)}
                              </p>
                            )}
                          </div>
                          
                          {/* Badges */}
                          <div className="flex flex-col gap-2 ml-4">
                            {getSourceBadge(video)}
                          </div>
                        </div>
                        
                        {/* Action Buttons */}
                        <div className="flex gap-2 mt-3">
                          {video.dmmContentId && (
                            <a
                              href={generateAffiliateUrl(video.dmmUrl || `https://www.dmm.co.jp/digital/videoa/-/detail/=/cid=${video.dmmContentId}/`)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700"
                            >
                              FANZA
                            </a>
                          )}
                          {video.mgsUrl && (
                            <a
                              href={generateMGSAffiliateUrl(video.mgsUrl)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                            >
                              MGS
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            </div>
          )}

          {/* No Results Message */}
          {(!results.videos || !Array.isArray(results.videos) || results.videos.length === 0) && 
           (!results.actresses || !Array.isArray(results.actresses) || results.actresses.length === 0) && (
            <p className="text-center text-gray-500 py-8">
              検索結果がありません
            </p>
          )}

          {/* Pagination */}
          {results.pagination && results.pagination.totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-8">
              <button
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
                className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
              >
                前へ
              </button>
              <span className="px-4 py-2">
                {page} / {results.pagination.totalPages}
              </span>
              <button
                onClick={() => handlePageChange(page + 1)}
                disabled={page === results.pagination.totalPages}
                className="px-4 py-2 bg-gray-200 rounded disabled:opacity-50"
              >
                次へ
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Actress Card Component
const ActressCard = ({ actress }) => {
  return (
    <>
      {actress.profileImage ? (
        <img 
          src={actress.profileImage} 
          alt={actress.name} 
          className="w-full h-48 object-cover"
        />
      ) : (
        <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
          <span className="text-gray-500">No Image</span>
        </div>
      )}
      <div className="p-3">
        <h3 className="text-sm font-medium mb-1">{actress.name}</h3>
        {actress.japaneseName && actress.japaneseName !== actress.name && (
          <p className="text-xs text-gray-600 mb-1">{actress.japaneseName}</p>
        )}
        {actress.videoCount !== undefined && (
          <p className="text-xs text-gray-500">作品数: {actress.videoCount}</p>
        )}
      </div>
    </>
  );
};

// Actress List Item Component
const ActressListItem = ({ actress }) => {
  return (
    <div className="flex gap-4">
      {/* Thumbnail */}
      <div className="flex-shrink-0">
        {actress.profileImage ? (
          <img 
            src={actress.profileImage} 
            alt={actress.name} 
            className="w-16 h-16 object-cover rounded-full"
          />
        ) : (
          <div className="w-16 h-16 bg-gray-200 flex items-center justify-center rounded-full">
            <span className="text-gray-500 text-xs">No Image</span>
          </div>
        )}
      </div>
      
      {/* Details */}
      <div className="flex-1">
        <h4 className="font-medium mb-1">{actress.name}</h4>
        {actress.japaneseName && actress.japaneseName !== actress.name && (
          <p className="text-sm text-gray-600">{actress.japaneseName}</p>
        )}
        <div className="flex gap-4 text-sm text-gray-500 mt-1">
          {actress.videoCount !== undefined && (
            <span>作品数: {actress.videoCount}</span>
          )}
          {actress.birthdate && (
            <span>生年月日: {actress.birthdate}</span>
          )}
          {actress.height && (
            <span>身長: {actress.height}cm</span>
          )}
        </div>
      </div>
    </div>
  );
};

// Video Card Component
const VideoCard = ({ video }) => {
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ja-JP');
  };

  return (
    <>
      {video.coverImage ? (
        <img 
          src={video.coverImage} 
          alt={video.title} 
          className="w-full h-48 object-cover"
        />
      ) : (
        <div className="w-full h-48 bg-gray-200 flex items-center justify-center">
          <span className="text-gray-500">No Image</span>
        </div>
      )}
      <div className="p-3">
        <h3 className="text-sm font-medium line-clamp-2 mb-1">{video.title}</h3>
        <p className="text-xs text-gray-600">{video.productCode}</p>
        {video.actresses && video.actresses.length > 0 && (
          <p className="text-xs text-gray-600 mt-1">
            {video.actresses.join(', ')}
          </p>
        )}
        {video.releaseDate && (
          <p className="text-xs text-gray-500 mt-1">
            {formatDate(video.releaseDate)}
          </p>
        )}
      </div>
    </>
  );
};

export default UnifiedSearch;